import type { ReactNode } from 'react'

/* The small parts the account pages are built from.

   The storefront had no forms outside two modals, and both of them wrote
   their field styling as a local string. Three account pages doing the same
   would be four copies of one treatment, so it is written once here — same
   values, one place. */

/** The panel every account surface sits on: raised carbon, hairline, the
    same radius the cards use. */
export function AccountPanel({
  children,
  className = '',
}: {
  children: ReactNode
  className?: string
}) {
  return (
    <section className={`rounded-2xl border border-line bg-raised/60 p-6 sm:p-7 ${className}`}>
      {children}
    </section>
  )
}

/* Shared with every input below rather than passed in, so a field on the
   profile page and a field on the sign-in form cannot drift apart. */
export const FIELD_CLASS =
  'mt-2 w-full rounded-xl border border-line bg-raised px-4 py-3 text-[15px] text-platinum placeholder:text-dim focus:border-lime focus:outline-none disabled:cursor-not-allowed disabled:opacity-55'

/** A label, a control, and — when it has gone wrong — one line under it
    saying why. The wiring between the three is the part that is easy to get
    almost right and that a screen reader then reads as three unrelated
    things, so it is done once: the label points at the control and the
    control names its error. */
export function AccountField({
  label,
  htmlFor,
  error,
  hint,
  optional,
  children,
  className = '',
}: {
  label: string
  htmlFor: string
  error?: string
  hint?: string
  optional?: boolean
  children: ReactNode
  className?: string
}) {
  return (
    <div className={`min-w-0 ${className}`}>
      <label htmlFor={htmlFor} className="label-sm flex items-baseline gap-2 text-steel">
        {label}
        {optional ? <span className="text-[10.5px] tracking-normal normal-case text-dim">optional</span> : null}
      </label>

      {children}

      {error ? (
        <p id={`${htmlFor}-error`} role="alert" className="mt-2 text-[12.5px] text-red-300">
          {error}
        </p>
      ) : hint ? (
        <p className="mt-2 text-[12.5px] text-dim">{hint}</p>
      ) : null}
    </div>
  )
}

/** A figure and what it is. Four of these run across the top of the account
    page — the four numbers somebody signs in to check. */
export function StatCard({
  label,
  value,
  hint,
}: {
  label: string
  value: string | number
  hint?: string
}) {
  return (
    <div className="rounded-2xl border border-line bg-raised/60 px-5 py-4">
      <p className="label-sm text-steel">{label}</p>
      <p className="mt-2 font-display text-[26px] leading-none font-semibold tracking-[-0.02em] text-platinum tabular-nums">
        {value}
      </p>
      {hint ? <p className="mt-1.5 text-[12px] text-dim">{hint}</p> : null}
    </div>
  )
}

/* The vocabulary of a booking's progress, and of a month on a bank plan.
   Named here rather than at each call site: the two pages that print them
   should never disagree about what "verified" is called or what colour it
   is. */
const TONES = {
  positive: 'border-lime/40 bg-lime/10 text-lime',
  waiting: 'border-line bg-raised text-steel',
  attention: 'border-amber-400/35 bg-amber-400/10 text-amber-200',
  bad: 'border-red-500/30 bg-red-500/10 text-red-200',
} as const

export type Tone = keyof typeof TONES

export function Pill({ tone = 'waiting', children }: { tone?: Tone; children: ReactNode }) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11.5px] font-medium ${TONES[tone]}`}
    >
      {children}
    </span>
  )
}
